import { useState, useEffect } from 'react';
import { Clock, Play, Square, Search, X } from 'lucide-react';
import './ControlHorari.css';
import API_URL from '../config';

function ControlHorari() {
  const [registresHoraris, setRegistresHoraris] = useState([]);
  const [paquets, setPaquets] = useState([]);
  const [cronometreActiu, setCronometreActiu] = useState(false); // Estat per controlar si el cronòmetre està actiu o no
  const [tempsTranscorregut, setTempsTranscorregut] = useState(0); // Començar des de 0 segons
  const [paquetSeleccionat, setPaquetSeleccionat] = useState('');
  const [tipusTasca, setTipusTasca] = useState('Administració'); // Valor per defecte
  const [error, setError] = useState('');
  const [carregant, setCarregant] = useState(true);
  const [cercaPaquet, setCercaPaquet] = useState(''); 
  const [DesplegableObert, setDesplegableObert] = useState(false);
  const [inicialitzant, setInicialitzant] = useState(true); // Nou estat per controlar la càrrega inicial i que no es faci l'amago de mostrar el formulari si hi ha una tasca activa
  const [cercaRegistre, setCercaRegistre] = useState('');
  const [tipusCerca, setTipusCerca] = useState('tot'); // 'tot' per defecte, 'client', 'data', 'tasca' per filtrar

  useEffect(() => { // Carregar paquets i registres al obrir la pàgina
    carregarPaquets();
    carregarRegistres();
    comprovarTascaActiva();
  }, []);

  useEffect(() => { // Actualitzar el cronòmetre cada segon si està actiu
    let interval;
    if (cronometreActiu) {
      interval = setInterval(() => {
        setTempsTranscorregut(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval); // Netejar l'interval al parar el cronòmetre 
  }, [cronometreActiu]);

  useEffect(() => {
    const tancarDesplegable = (e) => { // Tancar el desplegable si es fa clic fora d'ell
      if (!e.target.closest('.contenidor-cerca-Desplegable')) { // Comprovar si el clic està fora del contenidor
        setDesplegableObert(false);
      }
    };

    if (DesplegableObert) {
      document.addEventListener('click', tancarDesplegable); 
    }

    return () => {
      document.removeEventListener('click', tancarDesplegable); 
    };
  }, [DesplegableObert]);

  const comprovarTascaActiva = async () => { // Comprovar si hi ha una tasca activa al carregar la pàgina i restaurar el cronòmetre i segeix des del temps correcte
    const token = sessionStorage.getItem('token');
    if (!token) {
      setInicialitzant(false);
      return;
  }

    try {
      const response = await fetch(`${API_URL}/api/gestio/hores/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const tascaActiva = data.find(registre => !registre.hora_final);
        
        if (tascaActiva) {
          setCronometreActiu(true);
          const inici = new Date(tascaActiva.hora_inici);
          const ara = new Date();
          const segons = Math.floor((ara - inici) / 1000);
          setTempsTranscorregut(segons); // Restaurar el temps transcorregut i continuar el cronòmetre sumant des d'allà
        }
      }
    } catch (err) {
      console.error('Error comprovant tasca activa:', err);
    } finally {
      setInicialitzant(false); // Finalitzar l'estat d'inicialització després de la comprovació
    }
  };

  const carregarPaquets = async () => { // Carregar paquets per al selector
    const token = sessionStorage.getItem('token');
    if (!token) {
      setError('No estàs autenticat');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/gestio/paquets/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPaquets(data);
      }
    } catch (err) {
      setError('Error carregant paquets');
    }
  };

  const carregarRegistres = async () => { // Carregar registres horaris per a la taula
    const token = sessionStorage.getItem('token');
    if (!token) {
      setCarregant(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/gestio/hores/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRegistresHoraris(data);
      }
    } catch (err) {
      setError('Error carregant registres');
    } finally {
      setCarregant(false);
    }
  };

  const iniciarTasca = async () => { // Iniciar una nova tasca i començar el cronòmetre
    if (!paquetSeleccionat) {
      setError('Has de seleccionar un paquet');
      return;
    }

    const token = sessionStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/gestio/hores/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        },
        body: JSON.stringify({
          accio: 'iniciar',
          paquet_id: paquetSeleccionat,
          tipus_tasca: tipusTasca
        })
      });

      if (response.ok) {
        setCronometreActiu(true);
        setTempsTranscorregut(0);
        setError('');
        carregarRegistres();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error iniciant tasca');
      }
    } catch (err) {
      setError('Error de connexió');
    }
  };

  const aturarTasca = async () => { // Aturar la tasca activa i reiniciar el cronòmetre
    const token = sessionStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/gestio/hores/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        },
        body: JSON.stringify({
          accio: 'aturar'
        })
      });

      if (response.ok) {
        setCronometreActiu(false);
        setTempsTranscorregut(0);
        setError('');
        carregarRegistres();
      }
    } catch (err) {
      setError('Error aturant tasca');
    }
  };

  const formatarTemps = (segons) => { // Fer el format per els segons a HH:MM:SS
    const hores = Math.floor(segons / 3600);
    const minuts = Math.floor((segons % 3600) / 60);
    const seg = segons % 60;
    return `${String(hores).padStart(2, '0')}:${String(minuts).padStart(2, '0')}:${String(seg).padStart(2, '0')}`; // Aquesta linea el que diu es que si hores/minuts/segons tenen menys de 2 digits, afegeix un 0 al principi
  };

  const calcularDurada = (horaInici, horaFinal) => { // Calcular la durada entre hora d'inici i hora final
    if (!horaFinal) return 'En curs';
    const inici = new Date(horaInici);
    const final = new Date(horaFinal);
    const diferencia = Math.floor((final - inici) / 1000);
    return formatarTemps(diferencia);
  };

  const paquetsFiltrats = paquets.filter(paquet => { // Filtrar paquets segons el text de cerca
    const textCerca = cercaPaquet.toLowerCase();
    return paquet.nom_client?.toLowerCase().includes(textCerca);
  })

  const registresFiltrats = registresHoraris.filter(registre => {
    if (!cercaRegistre) return true;
    
    const textCerca = cercaRegistre.toLowerCase();
    
    switch(tipusCerca) {
      case 'client':
        return registre.nom_client?.toLowerCase().includes(textCerca);
      case 'data':
        return new Date(registre.hora_inici).toLocaleDateString('ca-ES').includes(textCerca);
      case 'tasca':
        return registre.tipus_tasca?.toLowerCase().includes(textCerca);
      default: // 'tot'
        return (
          registre.nom_client?.toLowerCase().includes(textCerca) ||
          registre.tipus_tasca?.toLowerCase().includes(textCerca) ||
          new Date(registre.hora_inici).toLocaleDateString('ca-ES').includes(textCerca)
        );
    }
  });

  return (
    <div className="seccio-control-horari">
      <div className="capçalera-seccio">
        <div className="text-llista">
          <h1>Control horari</h1>
          <p>Gestiona el teu temps de treball de manera eficient.</p>
        </div>
      </div>

      {error && <div className="missatge-error">{error}</div>}

      <div className="panell-cronometre">
        <div className="cronometre-visual">
          <Clock size={48} className="icona-rellotge" />
          <div className="temps-display">
            {formatarTemps(tempsTranscorregut)}
          </div>
          {cronometreActiu && (
            <div className="tasca-info">
              Tasca en curs
            </div>
          )}
        </div>

        {inicialitzant ? (
          <div className="carregant-tasca">
            <p>Carregant...</p> 
          </div>
        ) :!cronometreActiu ? (
          <div className="formulari-inici"> 
            <div className="camp camp-cerca-paquet">  
              <label>Paquet</label>
              <div 
                className="contenidor-cerca-desplegable"
                onClick={(e) => e.stopPropagation()} 
              >
                <div className="input-cerca-paquet">
                  <Search size={18} className="icona-cerca" />
                  <input
                    type="text"
                    placeholder="Cerca per nom de client..."
                    value={cercaPaquet}
                    onChange={(e) => {
                      setCercaPaquet(e.target.value);
                      setDesplegableObert(true);
                    }}
                    onFocus={() => setDesplegableObert(true)}
                  />
                  {cercaPaquet && (
                    <button 
                      className="boto-netejar-cerca" 
                      onClick={() => {
                        setCercaPaquet('');
                        setPaquetSeleccionat('');
                        setDesplegableObert(false);
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {DesplegableObert && (
                  <div className="desplegable-paquets">
                    {paquetsFiltrats.length === 0 ? (
                      <div className="desplegable-item-buit">
                        No s'han trobat paquets amb "{cercaPaquet}"
                      </div>
                    ) : (
                      paquetsFiltrats.map(paquet => (
                        <div
                          key={paquet.id}
                          className={`desplegable-item ${paquetSeleccionat === paquet.id ? 'seleccionat' : ''}`}
                          onClick={() => {
                            setPaquetSeleccionat(paquet.id);
                            setCercaPaquet(paquet.nom_client);
                            setDesplegableObert(false);
                          }}
                        >
                          <strong>{paquet.nom_client}</strong>
                          <span className="sessions-info">{paquet.sessions_restants} sessions restants</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              {paquetSeleccionat && (
                <div className="paquet-seleccionat-info">
                  ✓ Paquet seleccionat
                </div>
              )}
            </div>

            <div className="camp">
              <label>Tipus de tasca</label>
              <input 
                type="text"
                value={tipusTasca}
                onChange={(e) => setTipusTasca(e.target.value)}
                placeholder="Ex: Entrenament, Planificació..."
              />
            </div>

            <button className="boto-iniciar" onClick={iniciarTasca}>
              <Play size={18} />
              Iniciar
            </button>
          </div>
        ) : (
          <button className="boto-aturar" onClick={aturarTasca}>
            <Square size={18} />
            Aturar
          </button>
        )}
      </div>

      <div className="taula-contenidor">
        <div className="capçalera-llista-taula">
          <h2>Historial de registres</h2>
          <div className="grup-cerca-controls">
            <select 
              className="selector-tipus-cerca"
              value={tipusCerca}
              onChange={(e) => setTipusCerca(e.target.value)}
            >
              <option value="tot">Tot</option>
              <option value="client">Client</option>
              <option value="data">Data</option>
              <option value="tasca">Tipus tasca</option>
            </select>
            <div className="contenidor-cerca-llista">
              <Search size={18} />
              <input
                type="text"
                placeholder={`Cerca per ${tipusCerca === 'tot' ? 'client, data o tasca' : tipusCerca}...`}
                value={cercaRegistre}
                onChange={(e) => setCercaRegistre(e.target.value)}
              />
              {cercaRegistre && (
                <button 
                  className="boto-netejar-cerca" 
                  onClick={() => setCercaRegistre('')}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>


        {carregant ? (
          <p>Carregant dades...</p>
        ) : registresHoraris.length === 0 ? (
          <p className="missatge-buit">No hi ha registres horaris encara. Inicia la primera tasca!</p>
        ) : registresFiltrats.length === 0 ? (
          <p className="missatge-buit">No s'han trobat registres amb "{cercaRegistre}"</p>
        ) : (
          <table className="taula-personalitzada">
            <thead>
              <tr>
                <th>Client</th>
                <th>Tipus tasca</th>
                <th>Hora inici</th>
                <th>Hora final</th>
                <th>Duració</th>
              </tr>
            </thead>
            <tbody>
              {registresFiltrats.map(registre => (
                <tr key={registre.id}>
                  <td><strong>{registre.nom_client}</strong></td> 
                  <td>{registre.tipus_tasca}</td>
                  <td>{new Date(registre.hora_inici).toLocaleString('ca-ES')}</td>
                  <td>{registre.hora_final ? new Date(registre.hora_final).toLocaleString('ca-ES') : '-'}</td>
                  <td>{calcularDurada(registre.hora_inici, registre.hora_final)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ControlHorari;
